import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { CardModule } from 'primeng/card';

import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { ConfirmationService, MessageService } from 'primeng/api';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

interface Token {
  id?: string;
  subject: string;
  identityGroup: string;
  audienceGroup: string;
  scope: string;
  status: 'Active' | 'Expiring Soon' | 'Expired' | 'Revoked';
  issuedAt?: Date;
  expiresAt?: Date;
  ttl?: number;
}

@Component({
  selector: 'app-tokens-sessions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    TagModule,
    ProgressBarModule,
    CardModule,
    PageLayoutComponent,
    StateContainerComponent
  ],
  templateUrl: './tokens-sessions.component.html',
  providers: [MessageService, ConfirmationService]
})
export class TokensSessionsComponent implements OnInit {
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  tokens = signal<Token[]>([
    {
      id: '1',
      subject: 'API Reconciliation Bot',
      identityGroup: 'Machine identities',
      audienceGroup: 'SAP Integration APIs',
      scope: 'sap.read',
      status: 'Active',
      issuedAt: new Date(),
      expiresAt: new Date(Date.now() + 15 * 60000),
      ttl: 15
    },
    {
      id: '2',
      subject: 'Dealer Connect',
      identityGroup: 'Dealers',
      audienceGroup: 'Dealer Commerce APIs',
      scope: 'customers.read, orders.write',
      status: 'Active',
      issuedAt: new Date(),
      expiresAt: new Date(Date.now() + 15 * 60000),
      ttl: 15
    },
    {
      id: '3',
      subject: 'Riya Sharma',
      identityGroup: 'Vendors',
      audienceGroup: 'Vendor APIs',
      scope: 'orders.write',
      status: 'Expiring Soon',
      issuedAt: new Date(Date.now() - 13 * 60000),
      expiresAt: new Date(Date.now() + 2 * 60000),
      ttl: 2
    },
    {
      id: '4',
      subject: 'North Partner Network',
      identityGroup: 'Dealers',
      audienceGroup: 'Dealer Commerce APIs',
      scope: 'customers.read',
      status: 'Expired',
      issuedAt: new Date(Date.now() - 30 * 60000),
      expiresAt: new Date(Date.now() - 15 * 60000),
      ttl: 0
    }
  ]);

  filteredTokens = signal<Token[]>([]);
  loading = signal(false);
  error = signal<any>(null);
  searchTerm = '';
  statusFilter: string | null = null;
  sortField: string = '';
  sortOrder: number = 0;
  showDialog = false;

  statusFilterOptions = [
    { label: 'All Statuses', value: null },
    { label: 'Active', value: 'Active' },
    { label: 'Expiring Soon', value: 'Expiring Soon' },
    { label: 'Expired', value: 'Expired' },
    { label: 'Revoked', value: 'Revoked' }
  ];

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.filteredTokens().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!(this.searchTerm || this.statusFilter);
  });

  readonly emptyStateMessage = computed(() => {
    if (this.searchTerm || this.statusFilter) {
      return 'No tokens match your current search or filter criteria. Try adjusting your filters.';
    }
    return 'No tokens found. Issue a scoped token to get started.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load tokens. Please try again.';
  });

  ngOnInit() {
    this.filterTokens();
  }

  filterTokens() {
    let filtered = this.tokens();

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.subject.toLowerCase().includes(term) ||
        t.identityGroup.toLowerCase().includes(term) ||
        t.audienceGroup.toLowerCase().includes(term) ||
        t.scope.toLowerCase().includes(term)
      );
    }

    if (this.statusFilter) {
      filtered = filtered.filter(t => t.status === this.statusFilter);
    }

    this.filteredTokens.set(filtered);
  }

  // Retry method for error state
  retryLoad() {
    this.loading.set(true);
    this.error.set(null);
    setTimeout(() => {
      this.filterTokens();
      this.loading.set(false);
    }, 500);
  }

  clearFilters() {
    this.searchTerm = '';
    this.statusFilter = null;
    this.filterTokens();
  }

  refreshTokens() {
    this.filterTokens();
  }

  getActiveTokens(): number {
    return this.tokens().filter(t => t.status === 'Active' || t.status === 'Expiring Soon').length;
  }

  getActiveSessions(): number {
    // This would come from a service in a real app
    return 3892;
  }

  getStatusSeverity(status: string): string {
    const map: Record<string, string> = {
      'Active': 'success',
      'Expiring Soon': 'warning',
      'Expired': 'danger',
      'Revoked': 'danger'
    };
    return map[status] || 'info';
  }

  calculateTTLProgress(token: Token): number {
    if (token.status === 'Expired') return 0;
    if (token.status === 'Revoked') return 0;
    if (!token.ttl) return 100;
    // Assuming 15 min max TTL for progress calculation
    const maxTTL = 15;
    return Math.min((token.ttl / maxTTL) * 100, 100);
  }

  onSort(event: any) {
    const field = event.field;
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 1 ? -1 : this.sortOrder === -1 ? 0 : 1;
    } else {
      this.sortField = field;
      this.sortOrder = 1;
    }

    if (this.sortOrder === 0) {
      this.sortField = '';
      this.filterTokens();
      return;
    }

    const sorted = [...this.filteredTokens()].sort((a, b) => {
      const aVal = a[field as keyof Token] || '';
      const bVal = b[field as keyof Token] || '';
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return this.sortOrder === 1 ? comparison : -comparison;
    });

    this.filteredTokens.set(sorted);
  }

  openIssueTokenDialog() {
    this.messageService.add({
      severity: 'info',
      summary: 'Issue Token',
      detail: 'Token issuance dialog would open here'
    });
  }

  revokeToken(token: Token) {
    this.confirmationService.confirm({
      header: 'Confirm Revoke',
      message: `Are you sure you want to revoke token for "${token.subject}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.tokens.update(tokens =>
          tokens.map(t =>
            t.id === token.id ? { ...t, status: 'Revoked', ttl: 0 } : t
          )
        );
        this.filterTokens();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Token for "${token.subject}" revoked successfully`
        });
      }
    });
  }

  viewTokenDetails(token: Token) {
    this.messageService.add({
      severity: 'info',
      summary: 'Token Details',
      detail: `Viewing details for token: ${token.subject}`
    });
  }
}